<template>
  <!--
    A checkbox that belongs to this design system rather than to the operating
    system. The native one draws itself in the OS accent colour — bright blue
    on Windows next to a red brand — and there is no way to restyle it beyond
    `accent-color`, which does not cover the border, the radius or the tick.

    The input is still a real checkbox underneath: keyboard, form submission
    and screen readers all keep working. It is only the paint that is ours.
  -->
  <label class="check" :class="{ 'check--disabled': disabled }">
    <input
      :id="id"
      type="checkbox"
      class="check__input"
      :checked="modelValue"
      :disabled="disabled"
      :value="value"
      :aria-label="ariaLabel || undefined"
      :indeterminate="indeterminate"
      @change="$emit('update:modelValue', $event.target.checked)"
    />

    <span class="check__box" aria-hidden="true">
      <!--
        Two marks and not one. A box that is neither empty nor ticked has a
        third state to draw — «some of them» — and drawing it as empty is the
        version where somebody clicks the header box expecting to add the rest
        and instead clears the four they had already chosen.
      -->
      <svg v-if="indeterminate && !modelValue" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="3.5" stroke-linecap="round">
        <path d="M6 12h12" />
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 12.5l5 5L19 7" />
      </svg>
    </span>

    <span v-if="label || $slots.default" class="check__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script setup>
defineProps({
  modelValue: Boolean,
  label: String,
  id: String,
  value: [String, Number],
  disabled: Boolean,
  // «Some of them, not all». Its own state and not a third value of
  // `modelValue`, because that is what the DOM does and what a screen reader
  // announces — `aria-checked="mixed"` comes free from the native property.
  indeterminate: Boolean,
  // For a box with no visible label beside it: one in a table row, where the
  // name of what is being ticked is in the next cell. Named rather than left
  // to fall through, or the attribute lands on the <label> and the input — the
  // thing a screen reader actually reads — keeps saying nothing.
  ariaLabel: String,
})
defineEmits(['update:modelValue'])
</script>

<style scoped>
.check {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  user-select: none;
}
.check--disabled { cursor: not-allowed; opacity: 0.55; }

.check__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.check__box {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 1.5px solid var(--color-gray-300);
  border-radius: 5px;
  background: var(--color-surface);
  color: transparent;
  transition: background var(--fast), border-color var(--fast), color var(--fast);
}
.check__box svg { width: 12px; height: 12px; }

.check:hover .check__box { border-color: var(--color-primary); }

.check__input:checked + .check__box,
.check__input:indeterminate + .check__box {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-text-on-primary);
}

.check__input:focus-visible + .check__box {
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}

.check__label { font-size: var(--text-sm); }
</style>
